import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import Form from "../forms/form/Form";
import styles from "./HomePage.module.scss";
import validationSchema from "../../utilities/validationschema";
import { clinicState } from "../../globalState";
import Loader from "../Loader/Loader";
import Background from "../Background/Background";
import MultiButtonForm from "./MultiButtonForm";
import SingleButtonForm from "./SingleButtonForm";
import { oldObgynClinicSlugMap } from "../../assets/data/old-obgyn-clinics";
import { oldPromoClinicSlugMap } from "../../assets/data/old-promo-clinics";
import { LunaJoyFoundationService } from "../../services/LunajoyFoundationService";
import { Modal } from "antd";
import formsApplicationUrl from "../../utilities/urls";
import { Image } from 'antd'
import { useTranslation } from 'react-i18next';
import { getServiceUrl } from "../../utilities/getServiceUrl";
import { PARTNER_URL, BOOKING_URL } from "../../utilities/urls";

// Generate slug for all clinics including Old ob-gyn clinics and promo clinic
const getSlug = (clinic, typeOfScreening) => {
  let slug = oldObgynClinicSlugMap[clinic.clinicCode];
  if (slug && typeOfScreening === "obgyn") {
    return "/ob-gyn/" + slug;
  }
  slug = oldPromoClinicSlugMap[clinic.clinicCode];
  if (slug && typeOfScreening === "obgyn") {
    return "/ob-gyn/promo/" + slug;
  }
  return "/screening/" + clinic.screeningSlugs[typeOfScreening];
};

const getFormLink = (clinic, typeOfScreening) => {
  if (!clinic) {
    return `${formsApplicationUrl}/screening/${typeOfScreening}`;
  }
  const slug = getSlug(clinic, typeOfScreening);
  return formsApplicationUrl + slug;
};

const HomePage = () => {
  const { clinic } = useRecoilValue(clinicState);
  const [buttonPressed, setButtonPressed] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [buttons, setButtons] = useState({
    "General Assessment": { typeOfScreening: "phq9-gad7" },
  });

  // multilingual text
  const { t } = useTranslation()
  const pageTitle = t('title')
  const pageSubTitle = t('subTitle')
  const emailPlaceholder = t('emailPlaceholder')
  const emailNotFoundMessage = t('emailNotFoundMessage')
  const footerText1 = t('footerText1')
  const footerText2 = t('footerText2')
  const takeAssessmentButton = t('takeAssessmentButton')
  const phq9gad7Button = t('phq9gad7Button')
  const epdsButton = t('epdsButton')
  const phq2gad2Button = t('phq2gad2Button')

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const isPhq2Gad2 = buttons[buttonPressed]?.typeOfScreening === 'phq2-gad2'

    const clinicNameTag = clinic?.name
      ? `${clinic.name.toLowerCase()}-drop-off`
      : "general assessment-drop-off";

    const tags = [
      "landing page",
      ...(isPhq2Gad2 ? [] : [clinicNameTag]),
      ...((buttonPressed === "General Assessment" || buttonPressed === 'Evaluación General') ? ["general assessment-drop-off"] : [])
    ];

    let contact;

    // If phone input is not shown yet, find contact with email (without creating)
    if (!showPhoneInput) {
      const formData = {
        email: values?.email,
        from: "partner-landing-page",
      };
      contact = await LunaJoyFoundationService.findContactWithEmail(formData);
      
      // If contact not found, show phone input
      if (!contact?.id) {
        setShowPhoneInput(true);
        setIsLoading(false);
        return;
      }
    } else {
      // Phone input is shown, find or create contact by phone
      const formData = {
        phone: values?.phone,
        email: values?.email, // Include email for contact creation
        from: "partner-landing-page",
      };

      contact = await LunaJoyFoundationService.findOrCreateContactWithPhone(formData);
    }

    if (!contact?.id) {
      Modal.error({
        title: "Opps! We encountered an error!",
        content: <p>Please try again after sometime!</p>,
        okText: "OK",
        centered: true,
      });
      setIsLoading(false);
      return;
    }

    const existingSourceMechanism = contact?.customFields?.find((field) => field.id === 'tZ4KwxrA5GZO4Pe5CTPf')?.value
    const sourceMechanism = [...(existingSourceMechanism || []), 'Landing Page']
    const dropOffStatus = isPhq2Gad2 ? '' : 'Drop-off Personal Information'
    
    const updateFormData = {
      contact_id: contact.id,
      payload: {
        customFields: [
          {
            id: 'OUyyT8KElN9ScCWTw71p', // V4 Screening status
            field_value: 'Incomplete'
          },
          ...(dropOffStatus ? [{
            id: '7UOxRuf2jy43FYJP8azu', // Drop-off status
            field_value: dropOffStatus
          }] : []),
          {
            id: 'tZ4KwxrA5GZO4Pe5CTPf', // Source Mechanism
            field_value: sourceMechanism
          },
          {
            id: 'WDOtRl8vSDJpO0y92LiF', // Landing Page Link
            field_value: clinic?.clinicCode ? `${PARTNER_URL}/${clinic.clinicCode}` : `${PARTNER_URL}`
          },
          {
            id: 'NlcSAZaIqbAnGxLTQGTy', // Booking Link
            field_value: clinic?.clinicCode ? `${BOOKING_URL}/?referal=${clinic.clinicCode}` : `${BOOKING_URL}`
          }
        ],
        tags: tags,
      },
    };
    // Update the contact with the custom field data
    try {
      await LunaJoyFoundationService.updateGHLContact(updateFormData);
    } catch (updateError) {
      Modal.error({
        title: "Update Error!",
        content: <p>Contact was created but failed to update. Please try again.</p>,
        okText: "OK",
        centered: true,
      });
      setIsLoading(false);
      return;
    }

    let currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('email');
    currentUrl.searchParams.delete('phone');
    currentUrl.searchParams.delete('link');

    const newUrl = getFormLink(clinic, buttons[buttonPressed].typeOfScreening);
    const formattedPhone = contact?.phone?.startsWith('+1') ? contact?.phone.slice(2) : contact?.phone?.startsWith('1') ? contact?.phone.slice(1) : contact?.phone;

    const finalUrl = `${newUrl}?contactId=${contact.id}&email=${encodeURIComponent(values?.email)}&phone=${encodeURIComponent(formattedPhone)}&link=${encodeURIComponent(currentUrl.href)}`;

    setIsLoading(false);
    window.open(finalUrl, "_self", "noopener,noreferrer");
  };

  useEffect(() => {
    if (clinic) {
      const allButton = {};
      clinic.typesOfScreen.map((screening) => {
        if (screening === "Obgyn") {
          return { title: epdsButton, typeOfScreening: "obgyn" };
        } else if (screening === "PHQ9-GAD7") {
          return {
            title: phq9gad7Button,
            typeOfScreening: "phq9-gad7",
          };
        } else if (screening === "PHQ2-GAD2") {
          return {
            title: phq2gad2Button,
            typeOfScreening: "phq2-gad2",
          };
        }
        return null;
      })
      .forEach((button) => {
        if (button) {
          allButton[button.title] = {
            typeOfScreening: button.typeOfScreening,
          };
        }
      });
      setButtons(allButton);
    }
  }, [clinic, epdsButton, phq9gad7Button, phq2gad2Button]);

  return (
    <Background clinicDetails={clinic}>
      {isLoading ? <Loader /> : null}
      <div className={styles.mainContainer}>
        <div className={styles.linksContainer}>
          <a 
            href={getServiceUrl('booking', clinic?.clinicCode)} 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.actionLink}
          >
            Book an Appointment
          </a>
          <p className={styles.divider}>|</p>
          <a 
            href={getServiceUrl('referral', clinic?.clinicCode)} 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.actionLink}
          >
            Make Referral
          </a>
        </div>
        <div className={styles.container}>
          <h1 className={styles.heading}>{pageTitle}</h1>
          <div className={styles.content}>
            <p className={styles.para}>
              {pageSubTitle}
            </p>
            <Form onSubmit={handleFormSubmit} validateSchema={validationSchema}>
              {(renderProps) => (
                <>
                  {!clinic || clinic?.typesOfScreen?.length === 1 || (clinic?.typesOfScreen?.includes("Geriatric") && clinic?.typesOfScreen?.length === 2) ? (
                    <SingleButtonForm
                      buttonText={takeAssessmentButton}
                      value={Object.keys(buttons)[0]}
                      onClick={setButtonPressed}
                      placeholder={emailPlaceholder}
                      showPhoneInput={showPhoneInput}
                      emailNotFoundMessage={showPhoneInput ? emailNotFoundMessage : null}
                    />
                  ) : (
                    <MultiButtonForm
                      buttons={Object.keys(buttons)}
                      onClick={setButtonPressed}
                      placeholder={emailPlaceholder}
                      showPhoneInput={showPhoneInput}
                      emailNotFoundMessage={showPhoneInput ? emailNotFoundMessage : null}
                    />
                  )}
                </>
              )}
            </Form>
          </div>
          <div className={styles.bottomContainer}>
            <div className={styles.divider}></div>
            {clinic?.logo && <Image className={styles.dynamicLogo} src={`data:image/jpeg;base64,${btoa(String.fromCharCode(...new Uint8Array(clinic.logo?.data)))}`} alt={`${clinic.name} Logo`} preview={false} />}
            <div className={styles.bottomDescription}>
              <p>{footerText1}</p>
              <p>{footerText2}</p>
            </div>
          </div>
        </div>
      </div>
    </Background>
  );
};

export default HomePage;
